import { useEffect, useState } from "react";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Database, Download, Trash2, AlertTriangle, Shield, Wrench, Gift, Sparkles, FileSearch, ArrowLeft, History, Mail, RefreshCw, TrendingUp, Eye, MessageSquareWarning, Check, Archive, MessageCircleReply, Send, X, Plus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";
import { Section, KpiTile, SCOPE_LABELS } from "./_shared";
import { useConfirm } from "@/components/ConfirmDialog";

// ============================================================================
// FEEDBACK (beta tester inbox)
// ============================================================================
const FEEDBACK_STATUSES = ["new", "read", "resolved", "archived"];
const FEEDBACK_CATEGORY_LABEL = {
  bug: "🐛 Bug",
  suggestion: "💡 Sugestia",
  question: "❓ Pytanie",
  other: "📮 Inne",
};
const FEEDBACK_STATUS_STYLE = {
  new:       { bg: "#FEF08A", label: "NOWE" },
  read:      { bg: "#BFDBFE", label: "PRZECZYTANE" },
  resolved:  { bg: "#BBF7D0", label: "ROZWIĄZANE" },
  archived:  { bg: "#E2E8F0", label: "ARCHIWUM" },
};

function FeedbackTab() {
  const confirm = useConfirm();
  const [data, setData] = useState({ total: 0, rows: [], counts: {} });
  const [statusFilter, setStatusFilter] = useState("new");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null); // full feedback row
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      if (categoryFilter && categoryFilter !== "all") params.set("category", categoryFilter);
      params.set("limit", "100");
      const { data } = await teacherApi.get(`/admin/feedback?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error("Nie udało się pobrać zgłoszeń");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load();
  }, [statusFilter, categoryFilter]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await teacherApi.patch(`/admin/feedback/${id}`, { status });
      const suffix = data?.notified_reporter ? " (powiadomiono reportera 📧)" : "";
      toast.success(`Status zmieniony na: ${FEEDBACK_STATUS_STYLE[status].label}${suffix}`);
      load();
    } catch {
      toast.error("Nie udało się zmienić statusu");
    }
  };

  const removeRow = async (id) => {
    const ok = await confirm({
      title: "Usunąć zgłoszenie?",
      description: "To zgłoszenie zostanie trwale usunięte z inboxa. Operacja jest nieodwracalna.",
      confirmText: "Usuń trwale",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await teacherApi.delete(`/admin/feedback/${id}`);
      toast.success("Zgłoszenie usunięte");
      load();
    } catch {
      toast.error("Nie udało się usunąć");
    }
  };

  const sendReply = async () => {
    if (!replyTarget) return;
    const trimmed = replyText.trim();
    if (trimmed.length < 10) {
      toast.error("Wiadomość musi mieć co najmniej 10 znaków.");
      return;
    }
    setSending(true);
    try {
      await teacherApi.post(`/admin/feedback/${replyTarget.id}/reply`, {
        message: trimmed,
      });
      toast.success(`Odpowiedź wysłana do ${replyTarget.reporter_email} 📧`);
      setReplyTarget(null);
      setReplyText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udało się wysłać odpowiedzi.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Section icon={MessageSquareWarning} title="Feedback od beta-testerów">
      <p className="text-sm text-[#64748B] mb-4">
        Wszystkie zgłoszenia wysłane przez globalny przycisk „Feedback&rdquo;
        (dostępny na każdej stronie). Klasyfikacja: nowe → przeczytane →
        rozwiązane, albo archiwum. Endpoint publiczny <code>/api/feedback</code>
        jest rate-limited (5/godz./IP) — bezpieczny dla open beta.
      </p>

      {/* ---------- Status filter tabs with counts ---------- */}
      <div className="flex gap-2 mb-3 flex-wrap" data-testid="feedback-status-tabs">
        {[["all", "Wszystkie"], ...FEEDBACK_STATUSES.map((s) => [s, FEEDBACK_STATUS_STYLE[s].label])].map(([val, lbl]) => {
          const count = val === "all" ? data.counts?.total ?? 0 : data.counts?.[val] ?? 0;
          const isActive = statusFilter === val;
          return (
            <button
              key={val}
              onClick={() => setStatusFilter(val)}
              className={`brut brut-hover border-2 border-[#0F172A] rounded-md px-3 py-1.5 text-xs font-bold ${isActive ? "bg-[#0F172A] text-white" : "bg-white"}`}
              data-testid={`feedback-status-filter-${val}`}
            >
              {lbl} <span className="ml-1 opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {/* ---------- Category filter ---------- */}
      <div className="flex gap-2 mb-4 items-center flex-wrap">
        <span className="text-xs font-bold uppercase tracking-wider text-[#64748B]">Kategoria:</span>
        {["all", "bug", "suggestion", "question", "other"].map((c) => {
          const lbl = c === "all" ? "Wszystkie" : FEEDBACK_CATEGORY_LABEL[c];
          const isActive = categoryFilter === c;
          return (
            <button
              key={c}
              onClick={() => setCategoryFilter(c)}
              className={`brut brut-hover border-2 border-[#0F172A] rounded-md px-2.5 py-1 text-xs font-bold ${isActive ? "bg-[#FEF08A]" : "bg-white"}`}
              data-testid={`feedback-cat-filter-${c}`}
            >
              {lbl}
            </button>
          );
        })}
        <div className="flex-1" />
        <Button
          size="sm"
          onClick={load}
          disabled={loading}
          className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="feedback-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} strokeWidth={2.5} />
          Odśwież
        </Button>
      </div>

      {/* ---------- Rows ---------- */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto" data-testid="feedback-list">
        {data.rows.length === 0 && (
          <div className="text-sm text-[#64748B] italic">Brak zgłoszeń spełniających kryteria.</div>
        )}
        {data.rows.map((r) => {
          const style = FEEDBACK_STATUS_STYLE[r.status] || FEEDBACK_STATUS_STYLE.new;
          return (
            <div
              key={r.id}
              className="brut border-2 border-[#0F172A] rounded-md p-4 bg-white"
              data-testid={`feedback-row-${r.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span
                  className="brut border-2 border-[#0F172A] rounded-md px-2 py-0.5 text-[10px] font-black tracking-wider"
                  style={{ background: style.bg }}
                >
                  {style.label}
                </span>
                <span className="brut border-2 border-[#0F172A] rounded-md px-2 py-0.5 text-xs font-black bg-[#F1F5F9]">
                  {FEEDBACK_CATEGORY_LABEL[r.category] || r.category}
                </span>
                {r.resolved_notified_at && (
                  <span
                    className="brut border-2 border-[#0F172A] rounded-md px-2 py-0.5 text-[10px] font-black tracking-wider bg-[#DDD6FE]"
                    title={`Powiadomienie wysłane: ${new Date(r.resolved_notified_at).toLocaleString("pl")}`}
                    data-testid={`feedback-notified-badge-${r.id}`}
                  >
                    🔔 REPORTER POWIADOMIONY
                  </span>
                )}
                <span className="text-xs text-[#64748B] ml-auto">
                  {new Date(r.created_at).toLocaleString("pl")}
                </span>
              </div>
              <div className="font-black text-base break-words" data-testid={`feedback-subject-${r.id}`}>
                {r.subject}
              </div>
              <div className="text-sm mt-1 whitespace-pre-wrap break-words text-[#334155]">
                {r.message}
              </div>
              <div className="text-xs text-[#64748B] mt-2 space-y-0.5">
                <div>
                  <b>Od:</b> {r.reporter_name || "anonim"}
                  {r.reporter_email && (
                    <>
                      {" — "}
                      <a href={`mailto:${r.reporter_email}?subject=Re: ${encodeURIComponent(r.subject)}`}
                         className="underline font-bold">
                        {r.reporter_email}
                      </a>
                    </>
                  )}
                </div>
                {r.page_url && (
                  <div>
                    <b>Strona:</b> <a href={r.page_url} target="_blank" rel="noreferrer" className="underline break-all">{r.page_url}</a>
                  </div>
                )}
                <div><b>IP:</b> <code>{r.client_ip}</code></div>
              </div>

              <div className="flex gap-2 flex-wrap mt-3">
                {r.status === "new" && (
                  <Button
                    size="sm"
                    onClick={() => updateStatus(r.id, "read")}
                    className="brut brut-hover bg-[#BFDBFE] border-2 border-[#0F172A] font-bold rounded-md text-xs"
                    data-testid={`feedback-mark-read-${r.id}`}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} />
                    Oznacz jako przeczytane
                  </Button>
                )}
                {r.status !== "resolved" && (
                  <Button
                    size="sm"
                    onClick={() => updateStatus(r.id, "resolved")}
                    className="brut brut-hover bg-[#BBF7D0] border-2 border-[#0F172A] font-bold rounded-md text-xs"
                    data-testid={`feedback-mark-resolved-${r.id}`}
                  >
                    <Check className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} />
                    Rozwiązane
                  </Button>
                )}
                {r.status !== "archived" && (
                  <Button
                    size="sm"
                    onClick={() => updateStatus(r.id, "archived")}
                    className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md text-xs"
                    data-testid={`feedback-archive-${r.id}`}
                  >
                    <Archive className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} />
                    Archiwum
                  </Button>
                )}
                {r.reporter_email && (
                  <Button
                    size="sm"
                    onClick={() => { setReplyTarget(r); setReplyText(""); }}
                    className="brut brut-hover bg-[#DDD6FE] border-2 border-[#0F172A] font-bold rounded-md text-xs"
                    data-testid={`feedback-reply-btn-${r.id}`}
                  >
                    <MessageCircleReply className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} />
                    Odpowiedz osobiście
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => removeRow(r.id)}
                  className="brut brut-hover bg-[#FCA5A5] border-2 border-[#0F172A] font-bold rounded-md text-xs"
                  data-testid={`feedback-delete-${r.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} />
                  Usuń
                </Button>
              </div>

              {/* ---------- Reply history (if any) ---------- */}
              {Array.isArray(r.admin_replies) && r.admin_replies.length > 0 && (
                <div className="mt-3 pt-3 border-t-2 border-dashed border-[#CBD5E1]" data-testid={`feedback-replies-${r.id}`}>
                  <div className="text-xs font-black uppercase tracking-wider text-[#64748B] mb-2">
                    Historia odpowiedzi ({r.admin_replies.length})
                  </div>
                  <div className="space-y-2">
                    {r.admin_replies.map((rep) => (
                      <div key={rep.id} className="bg-[#F8FAFC] border-l-4 border-[#8B5CF6] pl-3 py-1.5 text-xs">
                        <div className="text-[#64748B] mb-0.5">
                          <b>{rep.sent_by_email || "admin"}</b>
                          {" · "}
                          {new Date(rep.sent_at).toLocaleString("pl")}
                        </div>
                        <div className="whitespace-pre-wrap text-[#334155]">{rep.message}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ---------- Reply modal ---------- */}
      {replyTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !sending && setReplyTarget(null)}
          data-testid="feedback-reply-modal"
        >
          <div
            className="brut border-4 border-[#0F172A] bg-white rounded-md max-w-2xl w-full p-6 shadow-[8px_8px_0_#0F172A]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="min-w-0">
                <div className="text-xs font-black uppercase tracking-wider text-[#64748B]">
                  Odpowiedź osobista
                </div>
                <div className="font-display font-black text-xl truncate">
                  Do: {replyTarget.reporter_name || "anonim"}
                </div>
                <div className="text-sm text-[#64748B] truncate">
                  {replyTarget.reporter_email}
                </div>
              </div>
              <button
                onClick={() => setReplyTarget(null)}
                disabled={sending}
                className="brut brut-hover bg-white border-2 border-[#0F172A] rounded-md p-1"
                data-testid="feedback-reply-close"
              >
                <X className="w-5 h-5" strokeWidth={2.5} />
              </button>
            </div>

            <div className="text-xs text-[#64748B] mb-2 border-l-4 border-[#FEF08A] pl-3 py-1 bg-[#FFFBEB]">
              <b>Oryginalny temat:</b> {replyTarget.subject}
              <br />
              <b>Treść:</b> <span className="italic">„{replyTarget.message.slice(0, 200)}{replyTarget.message.length > 200 ? "…" : ""}&rdquo;</span>
            </div>

            <label className="block text-xs font-black uppercase tracking-wider text-[#64748B] mb-1">
              Twoja odpowiedź (min. 10 znaków)
            </label>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={8}
              disabled={sending}
              maxLength={5000}
              placeholder="Cześć! Dziękuję za zgłoszenie — chciałbym doprecyzować…"
              className="w-full brut border-2 border-[#0F172A] rounded-md p-3 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
              data-testid="feedback-reply-textarea"
            />
            <div className="text-xs text-[#64748B] mt-1 text-right">
              {replyText.length}/5000
            </div>

            <div className="flex gap-2 mt-4 justify-end">
              <Button
                onClick={() => setReplyTarget(null)}
                disabled={sending}
                className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
                data-testid="feedback-reply-cancel"
              >
                Anuluj
              </Button>
              <Button
                onClick={sendReply}
                disabled={sending || replyText.trim().length < 10}
                className="brut brut-hover bg-[#8B5CF6] text-white border-2 border-[#0F172A] font-bold rounded-md"
                data-testid="feedback-reply-send"
              >
                <Send className={`w-4 h-4 mr-2 ${sending ? "animate-pulse" : ""}`} strokeWidth={2.5} />
                {sending ? "Wysyłanie…" : "Wyślij odpowiedź"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}




export default FeedbackTab;
